import { useCallback, useRef, useState } from 'react';

import api, { FetchError } from '../../api/api';
import { Platform } from 'react-native';

enum FetchMethod {
  Get = 'GET',
  Patch = 'PATCH',
  Post = 'POST',
  Put = 'PUT',
  Delete = 'DELETE'
}

interface ApiParams {
  [key: string]: any;
}

const FETCH_TIMEOUT_MS = 15000;
const RETRY_DELAYS_MS = [500, 1500];
// Only idempotent reads are auto-retried on a transport failure, so we never
// risk double-submitting a mutation that the server may have already applied.
const RETRYABLE_METHODS = new Set<FetchMethod>([FetchMethod.Get]);

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function useHttpFetch(baseUrl?: string, options?: RequestInit) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<FetchError>();
  const hookOptionsRef = useRef(options || {});
  hookOptionsRef.current = options || {};
  const performFetchUrl = useCallback(
    async (method: FetchMethod, url: URL, options?: RequestInit) => {
      setLoading(true);
      if (!options) options = {};
      options.method = method;
      if (options.headers) {
        options.headers = {
          ...hookOptionsRef.current.headers,
          ...options.headers,
          platform: Platform.OS
        };
      }
      // Cold-start calls (the first request after launch) can fail at the
      // transport layer before connectivity is fully ready, surfacing as
      // "TypeError: Network request failed" (status 0). Apply a timeout so a
      // stalled request can't hang forever, and retry idempotent reads with
      // backoff so a transient failure self-heals instead of becoming a hard
      // error / unhandled rejection.
      const callerSignal = options.signal;
      const maxAttempts = RETRYABLE_METHODS.has(method)
        ? RETRY_DELAYS_MS.length + 1
        : 1;
      let lastError: unknown;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (attempt > 0) await sleep(RETRY_DELAYS_MS[attempt - 1]);
        const controller = new AbortController();
        const timeoutId = setTimeout(
          () => controller.abort(),
          FETCH_TIMEOUT_MS
        );
        try {
          const result = await fetch(url.toString(), {
            ...hookOptionsRef.current,
            ...options,
            signal: callerSignal ?? controller.signal
          });
          clearTimeout(timeoutId);
          setLoading(false);
          return result;
        } catch (error) {
          clearTimeout(timeoutId);
          lastError = error;
          // Respect explicit caller cancellation — don't retry past it.
          if (callerSignal?.aborted) break;
        }
      }
      setLoading(false);
      setError(lastError as FetchError);
      throw lastError;
    },
    []
  );
  const performFetch = useCallback(
    async (method: FetchMethod, path: string, options?: RequestInit) => {
      const url = new URL(path, baseUrl);
      return performFetchUrl(method, url, options);
    },
    [baseUrl, performFetchUrl]
  );
  const get = useCallback(
    async (path: string, options?: RequestInit) => {
      return performFetch(FetchMethod.Get, path, options);
    },
    [performFetch]
  );
  const del = useCallback(
    async (path: string, options?: RequestInit) => {
      return performFetch(FetchMethod.Delete, path, options);
    },
    [performFetch]
  );
  const patch = useCallback(
    async (path: string, options?: RequestInit) => {
      return performFetch(FetchMethod.Patch, path, options);
    },
    [performFetch]
  );
  const post = useCallback(
    async (path: string, options?: RequestInit) => {
      return performFetch(FetchMethod.Post, path, options);
    },
    [performFetch]
  );
  const put = useCallback(
    async (path: string, options?: RequestInit) => {
      return performFetch(FetchMethod.Put, path, options);
    },
    [performFetch]
  );
  return {
    loading,
    error,
    performFetchUrl,
    performFetch,
    setError,
    get,
    del,
    patch,
    post,
    put
  };
}

export function useHttpFetchApi(baseUrl?: string, options?: RequestInit) {
  if (!baseUrl) baseUrl = api.environment.apiBaseUrl;
  if (!baseUrl) {
    throw new Error(
      '`api.environment` not setup properly. Missing `apiBaseUrl`.'
    );
  }
  if (!options) options = {};
  if (!options.headers) options.headers = {};
  const headers = options.headers as any;
  if (!headers['content-type']) headers['content-type'] = 'application/json';
  if (!headers[api.environment.accessTokenKey] && api.accessToken)
    headers[api.environment.accessTokenKey] = `Bearer ${api.accessToken}`;

  const { performFetchUrl, setError, ...httpFetchApi } = useHttpFetch(
    baseUrl,
    options
  );
  const [loading, setLoading] = useState(false);
  const performFetchUrlJson = useCallback(
    async <T>(method: FetchMethod, url: URL, options?: RequestInit) => {
      setLoading(true);
      const result = await performFetchUrl(method, url, options);
      let json: any;
      if (!result.ok) {
        // result.json() throws on non-JSON bodies (e.g. 502 HTML gateway responses)
        try { json = await result.json(); } catch { /* non-JSON error body */ }
        setError(
          json ?? {
            statusCode: result.status,
            error: result.statusText || 'RequestFailed',
            message: result.statusText || `Request failed (${result.status})`
          }
        );
        setLoading(false);
        return;
      }
      json = await result.json();
      if (json) json = parseDateFields(json);
      setLoading(false);
      setError(undefined);
      return json as T;
    },
    [performFetchUrl, setError]
  );
  const getJson = useCallback(
    async <T>(path: string, params?: ApiParams, options?: RequestInit) => {
      const url = new URL(path, baseUrl);
      appendAsQueryString(url, params);
      return performFetchUrlJson<T>(FetchMethod.Get, url, options);
    },
    [baseUrl, performFetchUrlJson]
  );
  const deleteJson = useCallback(
    async <T>(path: string, params?: ApiParams, options?: RequestInit) => {
      const url = new URL(path, baseUrl);
      appendAsQueryString(url, params);
      return performFetchUrlJson<T>(FetchMethod.Delete, url, options);
    },
    [baseUrl, performFetchUrlJson]
  );
  const patchJson = useCallback(
    async <T>(path: string, params?: ApiParams, options?: RequestInit) => {
      const url = new URL(path, baseUrl);
      if (!options) options = {};
      options.body = JSON.stringify(params);
      return performFetchUrlJson<T>(FetchMethod.Patch, url, options);
    },
    [baseUrl, performFetchUrlJson]
  );
  const postJson = useCallback(
    async <T>(path: string, params?: ApiParams, options?: RequestInit) => {
      const url = new URL(path, baseUrl);
      if (!options) options = {};
      options.body = JSON.stringify(params);
      return performFetchUrlJson<T>(FetchMethod.Post, url, options);
    },
    [baseUrl, performFetchUrlJson]
  );
  const putJson = useCallback(
    async <T>(path: string, params?: ApiParams, options?: RequestInit) => {
      const url = new URL(path, baseUrl);
      if (!options) options = {};
      options.body = JSON.stringify(params);
      return performFetchUrlJson<T>(FetchMethod.Put, url, options);
    },
    [baseUrl, performFetchUrlJson]
  );

  return {
    ...httpFetchApi,
    setError,
    loading,
    performFetchUrl,
    performFetchUrlJson,
    getJson,
    deleteJson,
    patchJson,
    postJson,
    putJson
  };
}

function appendAsQueryString(url: URL, params?: ApiParams) {
  Object.entries(params || {}).forEach(([key, value]) => {
    if (typeof value === 'undefined') return; // skip undefined
    url.searchParams.set(
      key,
      typeof value === 'object' ? JSON.stringify(value) : value
    );
  });
}

const DATE_STRING_REGEX =
  /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/;
/**
 * Recursively checks the response json object for Date properties in string format and parses them to native JS Date type
 * @param json
 */
export function parseDateFields(json: any): any {
  if (json === null || json === undefined) {
    return json;
  }
  if (Array.isArray(json)) {
    return json.map(item => parseDateFields(item));
  }
  if (typeof json === 'object') {
    const newJson: { [key: string]: any } = {};
    Object.keys(json).forEach((key: string) => {
      newJson[key] = parseDateFields(json[key]);
    });
    return newJson;
  }
  if (typeof json === 'string' && DATE_STRING_REGEX.test(json)) {
    return new Date(json);
  }
  return json;
}
